/**
 * Input ownership — THE seam that answers one question: *does the UI own the keyboard right now?*
 *
 * THE LAW (CLAUDE.md): every global game key consults `uiHasFocus()` BEFORE it acts AND BEFORE it
 * calls `preventDefault()`, and gameplay movement reads NEUTRAL while the answer is true. This is
 * not a nicety — it is the fix for a shipped bug. `Tab` was bound globally as a movement-preset
 * A/B swap; the encounter card pulls focus onto its buttons, so one Tab inside a card silently
 * swapped the live tuning to `ue-parity` (maxRunSpeed 7.0 → 4.3, groundAccel 48 → 20) and
 * `preventDefault()`ed the focus move, so there was no on-screen tell at all. Owner-reported as
 * "the game gets worse after every artifact"; reproduced in a real browser (perf diagnosis §2).
 *
 * WHY THE PREDICATE LIVES BEHIND A PROBE instead of being imported straight from its owner:
 * `src/ui/layers.ts` is the layer registry and KNOWS the answer, but it is lazy-chunk-only —
 * `scripts/checkBundle.mjs` fails the build if the `ui-layers` marker reaches the eager entry
 * chunk, so eager code (mount.ts, the input sources) may not import it. The registry therefore
 * PUBLISHES its own `uiHasFocus` here at module load (`setUiFocusProbe`) and eager code READS it
 * here. One predicate, one owner, one eager-safe door.
 *
 * No probe registered — any world whose `requires` names no exploration class, so the UI chunk
 * never downloads — answers `false`: the game owns the keyboard, which is exactly right when
 * there is no UI at all.
 *
 * ⚠️ AND SINCE L4 THAT PARAGRAPH DESCRIBES A CASE THAT NO LONGER HAPPENS ON A REAL BOOT. The
 * LOADING SURFACE (`src/ui/loading/`) is reached from the first statement of `mount()` on EVERY
 * world, so `src/ui/layers.ts` — and therefore this probe — is now registered on `sandbox-01`,
 * `playground-01` and `sponza-debug-01` too, not only on exploration worlds. Two consequences,
 * both deliberate and both stated here rather than left to be discovered:
 *
 *   1. `uiHasFocus()` is TRUE for the duration of every load, because the plate is a visible child
 *      of the input-owning `cutscene` layer. F2/F3/Alt+P are inert while the level is half-built
 *      and gameplay movement reads neutral — which is the point: the binder's own comment in
 *      `mount.ts` documents an F2 during Sponza's bind leaving the level hollow with no error.
 *      The plate hands the keyboard back by REPARENTING to `hud` the moment the gate opens, one
 *      frame before its fade starts, so the answer is false again for the player's first frame of
 *      actual play — not 620 ms later.
 *   2. The resolution controller's `ui` freeze path now runs on every boot. A resolution pop
 *      during boot is a worse surface than a frozen scale during boot, so this is the right trade,
 *      but it does put `tests/resolution.test.ts`'s known load-order flake on watch.
 *
 * The `null` arm below is kept, and it is not dead: a bare unit test constructs input sources with
 * no UI chunk anywhere, and a boot whose loading chunk fails to fetch must still be playable.
 */

/** What the layer registry publishes: "a UI surface owns the keyboard". */
export type UiFocusProbe = () => boolean;

let probe: UiFocusProbe | null = null;

/**
 * Registry door — `src/ui/layers.ts` calls this once at module load with its own predicate.
 * `null` clears it (tests, and any future teardown). Nothing else may call this.
 */
export function setUiFocusProbe(next: UiFocusProbe | null): void {
  probe = next;
}

/**
 * Does a UI surface own the keyboard right now? Never throws (rule 6) — a probe that blows up
 * hands the keyboard back to the game rather than freezing the player in place.
 */
export function uiHasFocus(): boolean {
  if (probe === null) return false;
  try {
    return probe() === true;
  } catch {
    return false;
  }
}
